import {
  SOCKET_AUTH_RETRY_BASE_DELAY_MS,
  SOCKET_AUTH_RETRY_MAX_DELAY_MS,
} from "../constants/socket-auth-recovery";
import type {
  IAppActivitySource,
  IRecordRefreshFailureProps,
  ISocketAuthRecoveryProps,
  ISocketAuthRecoveryTimers,
  TSocketAuthRecoveryDecision,
  TTimerHandle,
} from "./socket-auth-recovery.types";

const globalTimers: ISocketAuthRecoveryTimers = {
  setTimeout: ({ callback, delayMs }): TTimerHandle =>
    globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle: TTimerHandle): void => {
    globalThis.clearTimeout(handle);
  },
};

// NOTE: NSAI-5910 — paces how a socket recovers when the server refuses its
// handshake for its credentials. Each refusal used to be answered with a token
// refresh and an immediate reconnect, with no limit: if the refresh succeeded
// but the handshake was still refused, the socket cycled refresh + handshake
// as fast as the network allowed, in a hidden tab too, until the tab was
// closed. Now only the first refusal is answered at once. Every further one —
// and every failed refresh — waits: on a doubling timer while the app is in
// front of the user, and for the app to be used again while it is not. A
// handshake that goes through resets the count.
//
// Shared by the chat socket (WebSocketService) and both apps' notifications
// sockets, so web and mobile follow one policy.
export class SocketAuthRecovery {
  private readonly recover: () => void;
  private readonly activitySource?: IAppActivitySource;
  private readonly timers: ISocketAuthRecoveryTimers;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  // NOTE: Refusals and failed refreshes since the last successful handshake.
  private failureCount = 0;
  private retryTimer: TTimerHandle | null = null;
  private unsubscribeActivity: (() => void) | null = null;

  constructor({
    recover,
    activitySource,
    timers = globalTimers,
    baseDelayMs = SOCKET_AUTH_RETRY_BASE_DELAY_MS,
    maxDelayMs = SOCKET_AUTH_RETRY_MAX_DELAY_MS,
  }: ISocketAuthRecoveryProps) {
    this.recover = recover;
    this.activitySource = activitySource;
    this.timers = timers;
    this.baseDelayMs = baseDelayMs;
    this.maxDelayMs = maxDelayMs;
  }

  get isRetryPending(): boolean {
    return this.retryTimer !== null || this.unsubscribeActivity !== null;
  }

  get consecutiveFailures(): number {
    return this.failureCount;
  }

  // NOTE: The server refused the handshake for its credentials. `now` means
  // the caller refreshes and reconnects itself; `deferred` means a later
  // attempt is (already) scheduled and the caller must not refresh now.
  recordAuthRejection(): TSocketAuthRecoveryDecision {
    if (this.isRetryPending) {
      return "deferred";
    }

    const attempt = this.failureCount;
    this.failureCount += 1;

    if (attempt === 0) {
      return "now";
    }

    this.scheduleRetry(attempt);
    return "deferred";
  }

  // NOTE: The token refresh itself failed. A failure that ended the session
  // stops recovery for good — the API client has already signed the user out.
  // Anything else (rate limited, server or network error) is retried later,
  // never at once.
  recordRefreshFailure({ isSessionEnded }: IRecordRefreshFailureProps): void {
    if (isSessionEnded) {
      this.stop();
      return;
    }

    if (this.isRetryPending) {
      return;
    }

    this.failureCount += 1;
    this.scheduleRetry(this.failureCount - 1);
  }

  recordConnected(): void {
    this.stop();
  }

  // NOTE: Something outside the policy is reconnecting the socket right now
  // (a REST refresh produced a new token, or the user sent a message), so a
  // scheduled attempt would only duplicate it. The failure count is kept: if
  // that reconnect is refused as well, the next wait is longer, not reset.
  cancelPendingRetry(): void {
    if (this.retryTimer !== null) {
      this.timers.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (this.unsubscribeActivity) {
      this.unsubscribeActivity();
      this.unsubscribeActivity = null;
    }
  }

  stop(): void {
    this.cancelPendingRetry();
    this.failureCount = 0;
  }

  private getDelayMs(attempt: number): number {
    return Math.min(
      this.baseDelayMs * 2 ** Math.max(attempt - 1, 0),
      this.maxDelayMs,
    );
  }

  private isAppActive(): boolean {
    return this.activitySource?.isActive() ?? true;
  }

  private scheduleRetry(attempt: number): void {
    if (!this.isAppActive()) {
      this.waitForActivity();
      return;
    }

    this.retryTimer = this.timers.setTimeout({
      callback: () => {
        this.retryTimer = null;
        // NOTE: The app went to the background while this attempt waited —
        // nobody is looking, so wait for them instead of retrying unseen.
        if (!this.isAppActive()) {
          this.waitForActivity();
          return;
        }
        this.cancelPendingRetry();
        this.recover();
      },
      delayMs: Math.max(this.getDelayMs(attempt), 0),
    });
    // NOTE: Subscribe while the timer is pending so an app-return event does
    // not leave the socket waiting out the old deadline. The source must not
    // emit routine input events, which would bypass the retry backoff.
    this.waitForActivity();
  }

  private waitForActivity(): void {
    if (!this.activitySource || this.unsubscribeActivity) {
      return;
    }

    this.unsubscribeActivity = this.activitySource.subscribe(() => {
      if (!this.isAppActive()) {
        return;
      }
      this.cancelPendingRetry();
      this.recover();
    });
  }
}
